import { PointerActivationConstraints } from '@dnd-kit/dom';
import { DragDropProvider, KeyboardSensor, PointerSensor } from '@dnd-kit/react';
import { Book, ChevronDown, ChevronRight, Folder, FolderPlus, Plus, Settings } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { Button } from "@/components/ui/NewButton";
import type { Document } from "@/hooks/useDocuments";
import { tempId } from "@/utils/file-utils";
import { BundleDocumentSelectorDialog } from "../BundleDocumentSelectorDialog";
import {
  useAllFileGuids,
  useBundleCounts,
  useBundleTree,
  useBundleTreeActions,
  useBundleTreeStore,
  useEditorMeta,
  useHasExpandable,
} from "../bundleTreeStore";
import { GroupType, nextVolumeName } from "../bundleTypes";
import { EditEmailGroup } from "./EditEmailGroup";
import { EditFile } from "./EditFile";
import { EditSection } from "./EditSection";
import { EditVolume } from "./EditVolume";
import { findNearestAcceptedDroppable, GapDropZone, isNoOpDrop } from "./GapDropZone";

const ConfiguredPointerSensor = PointerSensor.configure({
  activationConstraints: [new PointerActivationConstraints.Distance({ value: 5 })],
});

type EditBundleTreeProps = {
  onOpenSettings?: () => void;
};

export const EditBundleTree = ({ onOpenSettings }: EditBundleTreeProps) => {
  const volumeNaming = useEditorMeta().metadata?.volume_naming ?? 'alpha';
  const matterGuid = useBundleTree((s) => s.matterGuid);
  const rootChildIds = useBundleTree((s) => s.config.rootChildIds);
  const expandedSize = useBundleTree((s) => s.expandedIds.size);
  const groupCount = useBundleTree((s) => Object.keys(s.config.groups).length);
  const { volumeCount, sectionCount, fileCount } = useBundleCounts();
  const { expandAll, collapseAll, addGroup, addDocuments } = useBundleTreeActions();
  const allFileGuids = useAllFileGuids();
  const hasExpandable = useHasExpandable();

  const [showDocPicker, setShowDocPicker] = useState(false);
  const [scrollToGroupId, setScrollToGroupId] = useState<string | null>(null);
  const allExpanded = expandedSize >= groupCount && groupCount > 0;

  // @dnd-kit re-subscribes sensors whenever the array reference changes. Memoize
  // so re-renders triggered by unrelated context updates (e.g. the side panel
  // opening for preview) don't tear down active drag handlers.
  const sensors = useMemo(() => [ConfiguredPointerSensor, KeyboardSensor], []);

  const handleAddRootDocuments = useCallback((docs: Document[]) => {
    const targetGroupId = addDocuments(docs);
    if (targetGroupId) {
      setScrollToGroupId(targetGroupId);
    }
    setShowDocPicker(false);
  }, [addDocuments]);

  const makeRootAcceptFn = (insertBeforeId?: string) => (source: any) => {
    if (!source?.data) return false;
    const config = useBundleTreeStore.getState().config;

    // Email attachments are locked inside their email group.
    if (source.data.type === 'file') {
      const parent = config.groups[source.data.groupId];
      if (parent?.groupType === GroupType.EMAIL) return false;
    }

    const sourceId = source.data.bundleFileId ?? source.data.groupId;
    if (!sourceId) return false;
    return !isNoOpDrop(sourceId, config.rootChildIds, insertBeforeId);
  };

  // Layout shifts (e.g. the side panel opening/closing) can leave @dnd-kit's cached
  // droppable shapes stale — the PositionObserver only fires on element mutations, not
  // when an ancestor's size changes. When drag starts we proactively refresh every
  // droppable's shape so collision detection works on accurate rects.
  const handleBeforeDragStart = (_event: any, manager: any) => {
    const droppables = manager?.registry?.droppables;
    if (!droppables) return;
    for (const d of droppables) {
      d.refreshShape?.();
    }
  };

  const handleDragEnd = (event: any, manager: any) => {
    const operation = manager?.dragOperation ?? event.operation;
    if (operation.canceled) return;
    const source = operation.source;
    const pointer = operation.position?.current;
    if (!source) return;

    // dnd-kit sets `operation.target` asynchronously; on fast drops the pointerup can fire
    // before it resolves. Fall back to computing the nearest accepted droppable ourselves.
    const target = operation.target
      ?? (pointer ? findNearestAcceptedDroppable(manager, source, pointer) : null);
    if (!target || target.data?.type !== 'gap') return;

    // Invariants (volume-only-at-root, email-attachment-locked, no-op drops, max depth, etc.)
    // are all enforced in the accept functions of each drop zone; any target that reaches us
    // here passed `d.accepts`.
    const { parentGroupId, insertBeforeId } = target.data;
    const sourceId = source.data.bundleFileId ?? source.data.groupId;
    useBundleTreeStore.getState().actions.moveChild(sourceId, parentGroupId, insertBeforeId);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <div className="flex items-center gap-1 mt-1">
            {hasExpandable && (
              <Button
                onClick={allExpanded ? collapseAll : expandAll}
                size="sm"
                variant="ghost"
                icon={allExpanded ? ChevronDown : ChevronRight}
                tooltip={allExpanded ? "Collapse all" : "Expand all"}
                className="text-gray-500 px-1.5 -ml-2"
              />
            )}
            <span className="text-xs text-gray-400 font-normal">
              {[
                volumeCount > 0 && `${volumeCount} volume${volumeCount !== 1 ? 's' : ''}`,
                sectionCount > 0 && `${sectionCount} section${sectionCount !== 1 ? 's' : ''}`,
                fileCount > 0 && `${fileCount} document${fileCount !== 1 ? 's' : ''}`,
              ].filter(Boolean).join(' · ')}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowDocPicker(true)}
            size="sm"
            variant="outline"
            icon={Plus}
            text="Add Documents"
          />
          <Button
            onClick={() => addGroup(tempId(), `Section ${sectionCount + 1}`, null, GroupType.SECTION)}
            size="sm"
            variant="outline"
            icon={FolderPlus}
            text="Add Section"
          />
          <Button
            onClick={() => {
              const config = useBundleTreeStore.getState().config;
              const volumeNames = config.rootChildIds
                .filter((id) => config.groups[id]?.groupType === GroupType.VOLUME)
                .map((id) => config.groups[id].title);
              addGroup(tempId(), nextVolumeName(volumeNaming, volumeNames), null, GroupType.VOLUME);
            }}
            size="sm"
            variant="outline"
            icon={Book}
            text="Add Volume"
          />
          {onOpenSettings && (
            <Button
              onClick={onOpenSettings}
              size="sm"
              variant="outline"
              icon={Settings}
              text="Settings"
            />
          )}
        </div>
      </div>
      {rootChildIds.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400 border rounded-lg bg-gray-50/50">
          <Folder className="h-10 w-10 mb-3 stroke-1" />
          <p className="text-sm font-medium mb-1">No volumes, sections or documents</p>
        </div>
      ) : (
        <DragDropProvider sensors={sensors} onBeforeDragStart={handleBeforeDragStart} onDragEnd={handleDragEnd}>
          <div className="space-y-1">
            {rootChildIds.map((childId) => (
              <RootChildRow
                key={childId}
                childId={childId}
                makeAcceptFn={makeRootAcceptFn}
                scrollToGroupId={scrollToGroupId}
                onScrollComplete={() => setScrollToGroupId(null)}
              />
            ))}
            <GapDropZone id="gap-root-end" parentGroupId={null} accept={makeRootAcceptFn()} />
          </div>
        </DragDropProvider>
      )}
      <BundleDocumentSelectorDialog
        open={showDocPicker}
        onOpenChange={setShowDocPicker}
        matterGuid={matterGuid}
        alreadyInBundleGuids={allFileGuids}
        onAddDocuments={handleAddRootDocuments}
      />
    </div>
  );
};

type RootChildRowProps = {
  childId: string;
  makeAcceptFn: (insertBeforeId?: string) => (source: any) => boolean;
  scrollToGroupId: string | null;
  onScrollComplete: () => void;
};

const RootChildRow = ({ childId, makeAcceptFn, scrollToGroupId, onScrollComplete }: RootChildRowProps) => {
  const file = useBundleTree((s) => s.config.files[childId]);
  const groupType = useBundleTree((s) => s.config.groups[childId]?.groupType);
  const scrollTo = scrollToGroupId === childId;
  return (
    <React.Fragment>
      <GapDropZone
        id={`gap-root-before-${childId}`}
        parentGroupId={null}
        insertBeforeId={childId}
        accept={makeAcceptFn(childId)}
      />
      {file ? (
        <EditFile file={file} />
      ) : groupType === GroupType.VOLUME ? (
        <EditVolume groupId={childId} scrollTo={scrollTo} onScrollComplete={onScrollComplete} />
      ) : groupType === GroupType.EMAIL ? (
        <EditEmailGroup groupId={childId} scrollTo={scrollTo} onScrollComplete={onScrollComplete} />
      ) : (
        <EditSection groupId={childId} scrollTo={scrollTo} onScrollComplete={onScrollComplete} />
      )}
    </React.Fragment>
  );
};
